import { requireUid } from './auth';
import { appeler } from './callable';
import { paths } from './paths';
import { loadUsers, unknownUser } from './records';
import { readOptionalValue, readValue } from './rtdb';
import type { PublicUser } from '@sirocco/shared';
import { t } from '../i18n';

/**
 * La file de moderation, vue du client.
 *
 * L'ecran ne decide de rien : il montre, et il appelle. Le role se
 * verifie dans la Cloud Function, pas ici — cacher un ecran n'empeche
 * personne d'appeler ce qu'il y a derriere. Ce que ce fichier fait, c'est
 * eviter d'afficher une porte fermee.
 */

export type RoleModeration = 'moderateur' | 'admin';

/** Ce que le moderateur peut faire d'un signalement. */
export type ActionModeration =
  | 'masquer'
  | 'classer'
  | 'suspendre'
  | 'retablir'
  | 'avertir'
  | 'museler'
  | 'liberer'
  | 'supprimer'
  | 'nommer'
  | 'verifier';

/**
 * Les trois gestes qu'un moderateur ne fait pas.
 *
 * Recopie de la fonction serveur, qui seule fait foi : ici la liste ne
 * sert qu'a ne pas montrer des boutons qui repondraient par un refus.
 */
export const RESERVEES_A_L_ADMIN: readonly ActionModeration[] = [
  'supprimer',
  'nommer',
  'verifier',
];

/** Un signalement, tel qu'il s'affiche dans la file. */
export interface Signalement {
  id: string;
  type: 'post' | 'comment' | 'event' | 'user' | 'conversation' | 'help';
  reason: string;
  details: string;
  createdAt: number;
  /** Present une fois le signalement traite. */
  closedAt: number | null;
  status: string | null;
  /** Qui a signale. */
  reporter: PublicUser;
  /** Qui est vise, quand le signalement vise quelqu'un. */
  vise: PublicUser | null;
  /** Combien de fois ce membre a deja ete signale, celui-ci compris. */
  signalementsDeLAuteur: number;
  /** Le texte du contenu vise, pour juger sans quitter l'ecran. */
  extrait: string;
  /** Le contenu est-il deja masque. */
  masque: boolean;
}

interface SignalementBrut {
  type?: Signalement['type'];
  postId?: string;
  commentId?: string;
  eventId?: string;
  userId?: string;
  conversationId?: string;
  serverId?: string;
  reporterId?: string;
  reason?: string;
  details?: string;
  message?: string;
  createdAt?: number;
  closedAt?: number;
  status?: string;
}

/** Mon role, s'il y en a un. `null` pour l'immense majorite des membres. */
export async function readMyRole(): Promise<RoleModeration | null> {
  const role = await readOptionalValue<string>(`roles/${requireUid()}`);
  return role === 'moderateur' || role === 'admin' ? role : null;
}

/**
 * Le contenu vise, resume.
 *
 * On lit la source plutot que de recopier le texte dans le signalement :
 * un contenu modifie depuis doit se juger tel qu'il est maintenant. Un
 * contenu disparu laisse une mention explicite — c'est une information,
 * pas une erreur.
 */
async function extraitDe(brut: SignalementBrut): Promise<{ texte: string; masque: boolean }> {
  const lire = async (chemin: string, champ: string) => {
    const valeur = await readOptionalValue<Record<string, unknown>>(chemin);
    if (!valeur) return { texte: t.moderation.contenu.supprime, masque: false };
    return {
      texte: String(valeur[champ] ?? '').trim() || t.moderation.contenu.sansTexte,
      masque: Boolean(valeur.masque),
    };
  };

  switch (brut.type) {
    case 'post':
      return lire(`${paths.posts(brut.serverId ?? '')}/${brut.postId}`, 'content');
    case 'comment':
      return lire(`comments/${brut.postId}/${brut.commentId}`, 'content');
    case 'event':
      return lire(`${paths.events(brut.serverId ?? '')}/${brut.eventId}`, 'title');
    case 'help':
      return { texte: (brut.message ?? '').trim() || '(sans texte)', masque: false };
    default:
      return { texte: '', masque: false };
  }
}

/** Qui est vise. Une conversation et un message d'aide ne visent personne. */
async function viseDe(brut: SignalementBrut): Promise<string | null> {
  switch (brut.type) {
    case 'user':
      return brut.userId ?? null;
    case 'post':
      return readValue<string>(`${paths.postIndex(brut.postId ?? '')}/authorId`);
    case 'comment':
      return readValue<string>(`comments/${brut.postId}/${brut.commentId}/authorId`);
    case 'event':
      return readValue<string>(`${paths.eventIndex(brut.eventId ?? '')}/organizerId`);
    default:
      return null;
  }
}

/**
 * La file entiere, du plus recent au plus ancien.
 *
 * Elle n'est lue que par un moderateur qui ouvre l'ecran : aucune ecoute,
 * aucun rafraichissement de fond. Une file de moderation n'a pas besoin
 * d'etre en temps reel, et elle porte le contenu de tout le monde.
 */
export async function listReports(): Promise<Signalement[]> {
  const tout = (await readOptionalValue<Record<string, SignalementBrut>>(paths.reports())) ?? {};
  const entrees = Object.entries(tout).sort(
    ([, a], [, b]) => (b.createdAt ?? 0) - (a.createdAt ?? 0),
  );

  const vises = await Promise.all(entrees.map(([, brut]) => viseDe(brut)));
  const extraits = await Promise.all(entrees.map(([, brut]) => extraitDe(brut)));

  const users = await loadUsers([
    ...entrees.map(([, brut]) => brut.reporterId ?? ''),
    ...vises.filter((uid): uid is string => Boolean(uid)),
  ]);

  const compteurs = await Promise.all(
    vises.map((uid) => (uid ? readOptionalValue<number>(`moderation/compteurs/${uid}`) : null)),
  );

  return entrees.map(([id, brut], index) => ({
    id,
    type: brut.type ?? 'post',
    reason: brut.reason ?? 'other',
    details: (brut.details ?? '').trim(),
    createdAt: brut.createdAt ?? 0,
    closedAt: brut.closedAt ?? null,
    status: brut.status ?? null,
    reporter: users.get(brut.reporterId ?? '') ?? unknownUser(brut.reporterId ?? ''),
    vise: vises[index] ? (users.get(vises[index]) ?? unknownUser(vises[index])) : null,
    signalementsDeLAuteur: compteurs[index] ?? 0,
    extrait: extraits[index].texte,
    masque: extraits[index].masque,
  }));
}

/** Ce qu'un geste porte en plus du motif, selon lequel c'est. */
export interface DetailsDuGeste {
  /** `avertir` : ce que le membre lira, mot pour mot. */
  texte?: string;
  /** `museler` : fin du silence en millisecondes, ou `null` sans terme. */
  jusqua?: number | null;
  /** `nommer` : le role a donner, ou `null` pour le retirer. */
  role?: RoleModeration | null;
  /** `verifier` : poser la coche, ou la retirer. */
  actif?: boolean;
}

/**
 * Un geste de moderation. Tout se decide et s'ecrit cote serveur.
 *
 * L'ecran ne fait que proposer : c'est la fonction qui verifie le role,
 * qui exige un motif, et qui refuse a un moderateur les trois gestes
 * reserves a l'administrateur. Cacher un bouton n'a jamais empeche
 * personne d'appeler ce qu'il y a derriere.
 */
export async function agir(
  action: ActionModeration,
  motif: string,
  reportId?: string,
  cible?: string,
  details: DetailsDuGeste = {},
): Promise<void> {
  await appeler<
    {
      action: ActionModeration;
      motif: string;
      reportId?: string;
      cible?: string;
    } & DetailsDuGeste,
    { ok: boolean }
  >('agirEnModeration', { action, motif, reportId, cible, ...details });
}

/** Ce qui pese sur un membre, tel que l'ecran d'administration le montre. */
export interface DossierDuMembre {
  role: RoleModeration | null;
  suspendu: boolean;
  verifie: boolean;
  /** Le silence en cours : `jusqua` a zero veut dire sans terme. */
  muet: { depuis: number; jusqua: number; motif: string } | null;
  avertissements: { at: number; texte: string; motif: string; lu: boolean }[];
  signalements: number;
}

/**
 * Tout ce qui pese sur un membre, en une lecture.
 *
 * Chaque branche est lue a part et son absence ne doit rien emporter :
 * un membre sans histoire n'a aucune de ces cles, et c'est le cas de
 * presque tout le monde.
 */
export async function lireLeDossier(uid: string): Promise<DossierDuMembre> {
  const [role, suspendu, muet, avertissements, compteur, verifie] = await Promise.all([
    readOptionalValue<string>(`roles/${uid}`),
    readOptionalValue<number>(`moderation/suspendus/${uid}`),
    readOptionalValue<{ depuis?: number; jusqua?: number; motif?: string }>(
      `moderation/muets/${uid}`,
    ),
    readOptionalValue<Record<string, { at?: number; texte?: string; motif?: string; lu?: boolean }>>(
      `moderation/avertissements/${uid}`,
    ),
    readOptionalValue<number>(`moderation/compteurs/${uid}`),
    readOptionalValue<boolean>(`users/${uid}/verifie`),
  ]);

  return {
    role: role === 'moderateur' || role === 'admin' ? role : null,
    suspendu: suspendu !== null,
    verifie: verifie === true,
    muet: muet
      ? { depuis: muet.depuis ?? 0, jusqua: muet.jusqua ?? 0, motif: muet.motif ?? '' }
      : null,
    avertissements: Object.values(avertissements ?? {})
      .map((a) => ({
        at: a.at ?? 0,
        texte: a.texte ?? '',
        motif: a.motif ?? '',
        lu: a.lu === true,
      }))
      .sort((a, b) => b.at - a.at),
    signalements: compteur ?? 0,
  };
}

/**
 * L'adresse electronique d'un membre.
 *
 * La fonction ne la rend qu'au proprietaire de l'application ; a tout
 * autre compte elle refuse, et l'ecran n'a simplement rien a montrer.
 */
export async function lireLAdresse(uid: string): Promise<string | null> {
  try {
    const { email } = await appeler<{ uid: string }, { email: string | null }>('adresseDuMembre', {
      uid,
    });
    return email;
  } catch {
    return null;
  }
}
